import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';

import DialogActions from 'components/DialogActions';
import ResponsiveDialogHeader from 'components/ResponsiveDialogHeader';
import DialogTransition from 'components/DialogTransition';
import SecondaryDialogButton from 'components/SecondaryDialogButton';
import { H6, Body1 } from 'components/Typography';
import isStringSet from 'utilities/is/isStringSet';
import isNotNullOrEmpty from 'utilities/is/isNotNullOrEmpty';
import styles from './styles';
import m from 'messages';

class CompetencyRequirementsDialog extends React.Component {

    splitByOrGroup(competences) {

        const allOf = [];
        const oneOf = {};

        competences.forEach(c => {
            if (!isStringSet(c.orGroup)) {
                allOf.push(c);
            } else {
                let group = oneOf[c.orGroup] || [];
                group.push(c);
                oneOf[c.orGroup] = group;
            }
        });

        let array = Object.keys(oneOf).map(k => oneOf[k]);

        return { allOf, oneOf: array };
    }

    requirementsList(requirements, groupName, contentBuilder) {
        const { allOf, oneOf } = this.splitByOrGroup(requirements);

        return (
            <React.Fragment>
                {requirements.length > 0 &&
                    <section className={this.props.classes.section}>
                        <H6>
                            {groupName}
                        </H6>
                        <div>
                            {isNotNullOrEmpty(allOf) &&
                                <React.Fragment>
                                    <Body1 className={this.props.classes.competenceGroupHeader}>{m.competencies.allOf}</Body1>
                                    {allOf.map((c, i) =>
                                        <React.Fragment key={i}>
                                            <Body1 className={this.props.classes.competence}>
                                                {contentBuilder(c)}
                                            </Body1>
                                            <Divider />
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            }
                        </div>
                        <div>
                            {isNotNullOrEmpty(oneOf) &&
                                <React.Fragment>
                                    {oneOf.map((group, i) =>
                                        <React.Fragment key={i}>
                                            <Body1 className={this.props.classes.competenceGroupHeader}>{m.competencies.oneOf}</Body1>
                                            {group.map((c, j) =>
                                                <React.Fragment key={j}>
                                                    <Body1 className={this.props.classes.competence}>
                                                        {contentBuilder(c)}
                                                    </Body1>
                                                    <Divider />
                                                </React.Fragment>
                                            )}
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            }
                        </div>
                    </section>
                }
            </React.Fragment>
        );

    }

    requirementPrefix = (nameExtract) => {
        return (c) => {
            let title = title => `${title}`;
            if (c.competenceID)
                title = m.competencies.requirementPrefix.competency;
            else if (c.jobRoleID)
                title = m.competencies.requirementPrefix.jobRole;
            return title(nameExtract(c));
        }
    }

    divisionRequirements() {

        const { divisionRequirements, divisionName } = this.props;
        let title = c => {
            if (c.divisionSpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.divisionName) ? `(${c.divisionName})` : ''}`;
            }

            if (c.companySpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.companyName) ? `(${c.companyName})` : ''}`;
            }

            return c.name;
        }
        title = this.requirementPrefix(title);

        const builder = c => <span>{title(c)}</span>;

        return this.requirementsList(divisionRequirements, divisionName, builder);
    }

    projectRequirements() {

        const { projectRequirements, projectLevelName } = this.props;
        let title = c => {
            if (c.projectSpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.projectName) ? `(${c.projectName})` : ''}`;
            }

            if (c.companySpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.companyName) ? `(${c.companyName})` : ''}`;
            }

            if (c.divisionSpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.divisionName) ? `(${c.divisionName})` : ''}`;
            }

            return c.name;
        }
        title = this.requirementPrefix(title);
        const builder = c => <span>{title(c)}</span>;

        return this.requirementsList(projectRequirements, projectLevelName, builder);
    }

    siteRequirements() {

        const { siteRequirements, siteLevelName } = this.props;
        let title = c => {
            if (c.siteSpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.siteName) ? `(${c.siteName})` : ''}`; 
            }

            if (c.companySpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.companyName) ? `(${c.companyName})` : ''}`; 
            }

            if (c.divisionSpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.divisionName) ? `(${c.divisionName})` : ''}`; 
            }

            return c.name;
        }
        title = this.requirementPrefix(title);
        const builder = c => <span>{title(c)}</span>;

        return this.requirementsList(siteRequirements, siteLevelName, builder);
    }

    zoneRequirements() {
        const { zoneRequirements, zoneLevelName } = this.props;
        let title = c => {
            if (c.zoneSpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.zoneName) ? `(${c.zoneName})` : ''}`;
            }

            if (c.companySpecific) {
                return `${c.name} ${isNotNullOrEmpty(c.companyName) ? `(${c.companyName})` : ''}`;
            }

            return c.name;
        }

        title = this.requirementPrefix(title);
        const builder = c => <span>{title(c)}</span>;

        return this.requirementsList(zoneRequirements, zoneLevelName, builder);
    }

    emptyMessage() {
        const count =
            this.props.zoneRequirements.length +
            this.props.siteRequirements.length +
            this.props.projectRequirements.length +
            this.props.divisionRequirements.length;

        return (
            <React.Fragment>
                {count === 0 &&
                    <Body1 className={this.props.classes.emptyMessage}>
                        {m.competencies.noRequirementsAtLocation}
                    </Body1>
                }
            </React.Fragment>
        );
    }

    render() {
        return (
            <Dialog
                open={this.props.open}
                onClose={this.props.handleClose}
                TransitionComponent={DialogTransition}
                fullScreen={this.props.fullScreen}
                className={this.props.classes.dialogRoot} >
                <ResponsiveDialogHeader
                    showBackIcon={false}
                    fullScreen={this.props.fullScreen}
                    title={m.competencies.locationRequirements}
                    fullScreenTitle=""
                    handleClose={this.props.handleClose} />
                <DialogContent className={this.props.classes.dialogContent}>
                    {this.divisionRequirements()}
                    {this.projectRequirements()}
                    {this.siteRequirements()}
                    {this.zoneRequirements()}
                    {this.emptyMessage()}
                </DialogContent>

                {!this.props.fullScreen &&
                    <DialogActions>
                        <SecondaryDialogButton onClick={this.props.handleClose}>
                            {m.general.close}
                        </SecondaryDialogButton>
                    </DialogActions>
                }
            </Dialog>
        );
    }
}

CompetencyRequirementsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
};

function mapStateToProps(state) {

    const { config, location, supervisor } = state;
    const {
        division = { divisionName: '' },
        project = { projectName: '' },
        site = { siteName: '' } } = location;
    const zone = location.zone || { zoneCode: '', zoneName: '' };
    
    const headerFunc = m.competencies.requirements.sectionHeader;

    return {
        divisionName: `${headerFunc(config.divisionLevelName, division.divisionName)}`,
        projectLevelName: `${headerFunc(config.projectLevelName, project.projectName)}`,
        siteLevelName: `${headerFunc(config.siteLevelName, site.siteName)}`,
        zoneLevelName: `${headerFunc(config.zoneLevelName, zone.zoneName)}`,
        hasZone: Boolean(zone.zoneCode),

        divisionRequirements: supervisor.divisionRequirements || [],
        projectRequirements: supervisor.projectRequirements || [],
        siteRequirements: supervisor.siteRequirements || [],
        zoneRequirements: supervisor.zoneRequirements || []
    };
}

export default
    withMobileDialog()(withStyles(styles)(connect(mapStateToProps)(CompetencyRequirementsDialog)));
